import { GetDashboardDataResponse } from '@consigli/api-client';
import { BetaBadge, Button, LoadingArea, PieChart, type PieChartDataItem } from '@consigli/facade';
import {
  useDocumentPackageSummary,
  useGetDashboardDataQuery,
  useGetFindingsQuery,
  usePackageByTypeAndPackageId,
  usePackageId,
  useProject,
  useProjectId,
  usePublishedPolling,
  useServiceName,
} from '@consigli/hooks';
import { ConflictCategory, ConflictType, DocumentPackageType, ServiceName } from '@consigli/types';
import { saveBlob } from '@consigli/utils';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { AdminBanner } from '@/components/admin-banner';
import { WaitingForAnalysis } from '@/components/waiting-for-analysis';
import { AnalysisPreviewOverlay } from '@/features/package-creation/components/analysis-preview/analysis-preview-overlay';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { ServiceIdIndexValue } from '@/routes';
import { useDemoMode } from '@/util/demo-mode';
import { serviceConfig, SHOW_BETA_BADGES } from '@/util/service-config';

import { DashboardCard } from './dashboard-card';
import {
  buildDashboardConflictSections,
  conflictSectionReactKey,
  conflictSectionTitleKey,
} from './dashboard-conflict-sections';
import { DashboardSearch } from './dashboard-search';
import { rfpFindingsQuery } from './rfp/rfp-findings-query';
import { RfpRequirementsAccordion } from './rfp/rfp-requirements-accordion';
import { RfpTimeline } from './rfp/rfp-timeline';
import { SummaryDescription } from '../summary/summary-description';

export const DashboardPage: FC = () => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const serviceName = useServiceName();
  const { t } = useTranslation();
  const { adminFeaturesEnabled } = useDemoMode();
  const { project } = useProject(projectId);

  const { pkg } = usePackageByTypeAndPackageId(
    projectId,
    ServiceIdIndexValue[serviceName] as DocumentPackageType,
    packageId,
  );
  const initialIsPublished = pkg ? pkg.published : false;

  const { isPublished } = usePublishedPolling(
    projectId,
    packageId,
    initialIsPublished,
    adminFeaturesEnabled,
  );

  const { data: rawData } = useGetDashboardDataQuery({ projectId, packageId });

  const [dashboardData, setDashboardData] = useState<GetDashboardDataResponse>();
  const [piechartData, setPieChartData] = useState<PieChartDataItem[]>();

  const isDesignPackageReview = serviceName === ServiceName.DESIGN_PACKAGE_REVIEW;
  const isRfpPackage = serviceName === ServiceName.RFP_RFQ_REVIEW;
  const isWaterService = serviceName === ServiceName.WATER_PROCESS_SYSTEMS_AGENT;
  const isBetaService =
    SHOW_BETA_BADGES && (serviceConfig.find((s) => s.serviceType === serviceName)?.isBeta ?? false);

  const { summary } = useDocumentPackageSummary(projectId, packageId);

  const handleExportSummary = useCallback(() => {
    if (!summary?.description || !project) return;
    try {
      const blob = new Blob([summary.description], { type: 'text/plain;charset=utf-8' });
      saveBlob(`${project.name}_summary.txt`, blob);
      toast.success(t('dashboard.rfp-export.success'));
    } catch {
      toast.error(t('dashboard.rfp-export.error'));
    }
  }, [summary?.description, project, t]);

  const { data: deliverableFindingsResponse } = useGetFindingsQuery(
    rfpFindingsQuery(projectId, packageId, ConflictType.DELIVERABLE),
    { skip: !isRfpPackage },
  );
  const deliverableFindings = useMemo(
    () => deliverableFindingsResponse?.results ?? [],
    [deliverableFindingsResponse],
  );

  const { data: milestoneFindingsResponse } = useGetFindingsQuery(
    rfpFindingsQuery(projectId, packageId, ConflictType.MILESTONE),
    { skip: !isRfpPackage },
  );
  const milestoneFindings = useMemo(
    () => milestoneFindingsResponse?.results ?? [],
    [milestoneFindingsResponse],
  );

  const dashboardConflictSections = useMemo(() => {
    return buildDashboardConflictSections(
      dashboardData?.conflictTypesCount,
      isDesignPackageReview,
      isRfpPackage,
    );
  }, [dashboardData?.conflictTypesCount, isDesignPackageReview, isRfpPackage]);

  const navToFindingsFiltered = useCallback(
    (conflictCategory: ConflictCategory | number, conflictType?: ConflictType) => {
      if (!conflictType) {
        return `../findings?conflictCategory=${conflictCategory}`;
      }
      return `../findings?conflictCategory=${conflictCategory}&conflictType=${conflictType}`;
    },
    [],
  );

  const generatePiechartData = useCallback(
    (data: Record<string, number>) => {
      const active = data?.ACTIVE ?? 0;
      const processing = data?.PROCESSING ?? 0;
      const processed = data?.PROCESSED ?? 0;
      const notRelevant = data?.NOT_RELEVANT ?? 0;
      const dataArray: PieChartDataItem[] = [
        {
          title: t('dashboard.not-relevant'),
          value: notRelevant,
          color: '#CBD5E1',
        },
        {
          title: t('dashboard.active'),
          value: active,
          color: '#475569',
        },
        {
          title: t('dashboard.processing'),
          value: processing,
          color: '#7588A3',
        },
        {
          title: t('dashboard.processed'),
          value: processed,
          color: '#ffffff',
        },
      ];
      return dataArray;
    },
    [t],
  );

  useEffect(() => {
    if (rawData) {
      setDashboardData(rawData);
      setPieChartData(generatePiechartData(rawData.statusPieChart));
    }
  }, [rawData, generatePiechartData]);

  const waitingForAnalysis = useMemo(() => {
    if (adminFeaturesEnabled) {
      return false;
    }
    if (isPublished) {
      return false;
    }
    return true;
  }, [isPublished, adminFeaturesEnabled]);

  return (
    <WorkspaceProvider>
      <WorkspaceLayout hideTabs={waitingForAnalysis}>
        {waitingForAnalysis ? (
          <>
            <WaitingForAnalysis />
            <AnalysisPreviewOverlay />
          </>
        ) : (
          <>
            {dashboardData && piechartData ? (
              <div className="px-8">
                <div className="flex flex-col">
                  <AdminBanner published={isPublished} />
                  <div className="flex flex-col lg:flex-row justify-between mt-4 gap-4">
                    <div className="flex flex-col basis-7/12 w-full">
                      <div className="flex items-center gap-3 pb-8">
                        <span className="text-day-neutral-subtle text-3xl font-semibold">
                          {pkg?.name}
                        </span>
                        {isBetaService && <BetaBadge />}
                      </div>
                      <div className="flex flex-col md:flex-row gap-8">
                        <DashboardCard
                          title={t('dashboard.analyzed-documents')}
                          body={`${dashboardData?.filesCount.processed}/${dashboardData?.filesCount.all}`}
                          navigateTo={'../documents'}
                        />
                        <DashboardCard
                          title={t('dashboard.duplicated-documents')}
                          body={dashboardData?.filesCount.duplicates}
                          navigateTo={'../documents'}
                        />
                      </div>
                      <DashboardSearch />
                    </div>
                    <PieChart
                      data={piechartData}
                      centerLabel={t('dashboard.pie-findings')}
                      className="bg-day-light-3 rounded-md p-6"
                    />
                  </div>
                  {isRfpPackage && (
                    <>
                      {summary?.description && (
                        <>
                          <div className="flex items-center justify-between py-4">
                            <h2 className="text-2xl font-semibold text-day-neutral-subtle">
                              {t('summary.title')}
                            </h2>
                            <Button secondary rounded onClick={handleExportSummary}>
                              {t('dashboard.rfp-export.export-summary')}
                            </Button>
                          </div>
                          <div className="bg-day-light-5 rounded-xl border border-day-light-2 shadow-(--shadow-card) px-6 py-5 text-sm text-day-neutral-dark leading-loose mb-6">
                            <SummaryDescription description={summary.description} />
                          </div>
                        </>
                      )}
                      <RfpTimeline findings={milestoneFindings} />
                      <RfpRequirementsAccordion findings={deliverableFindings} />
                    </>
                  )}
                  <div className="flex gap-3 flex-col my-6">
                    {dashboardConflictSections.map((section) => {
                      const sectionKey = conflictSectionReactKey(section);
                      return (
                        <div className="w-full" key={sectionKey}>
                          <div className="w-full">
                            <h3 className="text-2xl font-semibold text-day-neutral-subtle py-4">
                              {t(conflictSectionTitleKey(section, isWaterService))}
                            </h3>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 justify-between gap-8">
                            {section.cards.map(({ navConflictCategory, conflictType }) => {
                              const typeId = Object.keys(conflictType)[0];
                              const conflictTypeEnum =
                                ConflictType[typeId as keyof typeof ConflictType] || typeId;
                              const count = conflictType[typeId];
                              return (
                                <div key={`${sectionKey}-${navConflictCategory}-${typeId}`}>
                                  <DashboardCard
                                    title={t([
                                      `dashboard.conflict-title.${conflictTypeEnum}`,
                                      'dashboard.conflict-title.__fallback',
                                    ])}
                                    body={count}
                                    navigateTo={navToFindingsFiltered(
                                      navConflictCategory,
                                      conflictTypeEnum,
                                    )}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <LoadingArea />
            )}
          </>
        )}
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
